import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Corte das 7h (backend utils/auth.py, rodízio diário de 16/10/2026): todo dia às 7h
  // o acesso de todo mundo expira. Qualquer 401 com acesso guardado volta para o login,
  // com o aviso; sem isso a tela ficava aberta dando erro em toda ação.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const url = error?.config?.url || '';
        if (error?.response?.status === 401 && localStorage.getItem('token') && !url.includes('/api/auth/login')) {
          const detail = error?.response?.data?.detail;
          try {
            sessionStorage.setItem('elo_aviso_login',
              typeof detail === 'string' && detail.includes('7h') ? detail : 'Sua sessão terminou. Entre de novo.');
          } catch (e) { /* sem sessionStorage: segue sem o aviso */ }
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    // X-ELO-Tela: este acesso é de pessoa na tela e cai no corte das 7h (backend routes/auth.py)
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { headers: { 'X-ELO-Tela': '1' } });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isDashboardOnly = user?.role === 'dashboard';
  // Perfil "consulta": somente leitura, acessa apenas Dashboard, Atendimentos,
  // Pag. Não Aprovado e Cancelamentos (+ Perfil).
  const isConsulta = user?.role === 'consulta';
  // ETR e Erro na Nota sairam de dentro de /cancelamentos e ganharam rota
  // propria (10/09/2026) — sem entrar aqui, o perfil consulta veria o item no
  // menu e tomaria bloqueio ao clicar.
  const CONSULTA_PATHS = ['/dashboard', '/chamados', '/pagamento', '/cancelamentos',
                          '/etr', '/erro-nota', '/perfil'];

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, isDashboardOnly, isConsulta, CONSULTA_PATHS }}>
      {children}
    </AuthContext.Provider>
  );
};
